import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase, CalendarDays, Receipt, AlertCircle, TrendingUp, Clock, Wallet, FileWarning, Inbox,
  MessageCircle, Phone,
} from "lucide-react";
import { adminApi } from "../../lib/api";
import { money } from "../../lib/jobCalc";
import { openWhatsApp } from "../../lib/whatsapp";

const Stat = ({ icon: Icon, label, value, hint, color = "#f28a16", testid, to }) => {
  const Inner = (
    <div className={`bg-white border border-[#cfd7db] rounded-md p-5 shadow-sm ${to ? "hover:border-[#f28a16] hover:-translate-y-0.5 transition-card" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-display uppercase tracking-widest text-[11px] text-[#3f4b52]">{label}</div>
        <div className="h-9 w-9 rounded-md flex items-center justify-center" style={{ background: `${color}1a`, color }}>
          <Icon size={18} />
        </div>
      </div>
      <div className="font-display font-black text-3xl text-[#111619]">{value}</div>
      {hint && <div className="text-xs text-[#3f4b52] mt-1">{hint}</div>}
    </div>
  );
  return to ? <Link to={to}>{Inner}</Link> : Inner;
};

export const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [newQuotes, setNewQuotes] = useState(0);
  const [chase, setChase] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [d, jobs, bookings, quotes, chaseData] = await Promise.all([
          adminApi.dashboard(),
          adminApi.listJobs(),
          adminApi.listBookings({ status: "scheduled" }),
          adminApi.listQuotes({ status: "new" }),
          adminApi.chaseList({ limit: 6 }).catch(() => null),
        ]);
        setData(d);
        setRecent(jobs.slice(0, 6));
        const today = new Date().toISOString().slice(0, 10);
        setUpcoming(bookings.filter((b) => b.fitment_date >= today).slice(0, 6));
        setNewQuotes(quotes.length);
        setChase(chaseData);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Dashboard</h1>
      <p className="text-[#3f4b52] mt-1">Overview of fitments, invoices and bookings.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat icon={Inbox} label="New quote leads" value={loading ? "…" : newQuotes} hint="Click to open inbox" color="#f28a16" testid="stat-new-quotes" to="/admin/quotes" />
        <Stat icon={TrendingUp} label="Fitments this week" value={loading ? "…" : data?.weekly_fitments ?? 0} color="#f28a16" testid="stat-weekly" />
        <Stat icon={TrendingUp} label="This month" value={loading ? "…" : data?.monthly_fitments ?? 0} color="#10b981" testid="stat-monthly" />
        <Stat icon={TrendingUp} label="This year" value={loading ? "…" : data?.yearly_fitments ?? 0} color="#3b82f6" testid="stat-yearly" />

        <Stat icon={Briefcase} label="Total jobs" value={loading ? "…" : data?.total_jobs ?? 0} color="#20282c" testid="stat-total" />
        <Stat icon={Clock} label="Pending quotes" value={loading ? "…" : data?.pending_quotes ?? 0} color="#f28a16" testid="stat-pending" />
        <Stat icon={AlertCircle} label="In progress" value={loading ? "…" : data?.in_progress ?? 0} color="#3b82f6" testid="stat-inprogress" />
        <Stat icon={FileWarning} label="Outstanding invoices" value={loading ? "…" : data?.outstanding_invoices ?? 0} color="#dc2626" testid="stat-outstanding" to="/admin/aging" />
        <Stat icon={Wallet} label="Outstanding amount" value={loading ? "…" : money(data?.outstanding_amount ?? 0)} color="#dc2626" testid="stat-outstanding-amt" to="/admin/aging" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
        <div className="bg-white border border-[#cfd7db] rounded-md shadow-sm">
          <div className="px-5 py-4 border-b border-[#cfd7db] flex items-center justify-between">
            <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">Recent jobs</div>
            <Link to="/admin/jobs" className="text-xs text-[#f28a16] font-semibold hover:underline">View all →</Link>
          </div>
          <ul className="divide-y divide-[#eef2f4]">
            {recent.length === 0 && (
              <li className="px-5 py-6 text-sm text-[#3f4b52]">No jobs yet — create your first one.</li>
            )}
            {recent.map((j) => (
              <li key={j.id} className="px-5 py-3 flex items-center justify-between text-sm">
                <Link to={`/admin/jobs/${j.id}`} className="hover:text-[#f28a16]">
                  <span className="font-display font-bold">#{j.job_number}</span>{" "}
                  <span className="text-[#111619]">{j.client_name}</span>{" "}
                  <span className="text-[#3f4b52]">· {j.vehicle_make_model || "—"} · {j.registration || "—"}</span>
                </Link>
                <span className="text-xs text-[#3f4b52] uppercase tracking-wider">{j.status}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white border border-[#cfd7db] rounded-md shadow-sm">
          <div className="px-5 py-4 border-b border-[#cfd7db] flex items-center justify-between">
            <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">Upcoming bookings ({data?.upcoming_bookings ?? 0})</div>
            <Link to="/admin/bookings" className="text-xs text-[#f28a16] font-semibold hover:underline">Schedule →</Link>
          </div>
          <ul className="divide-y divide-[#eef2f4]">
            {upcoming.length === 0 && (
              <li className="px-5 py-6 text-sm text-[#3f4b52]">No upcoming bookings.</li>
            )}
            {upcoming.map((b) => (
              <li key={b.id} className="px-5 py-3 flex items-center justify-between text-sm">
                <div>
                  <span className="font-display font-bold text-[#f28a16]">{b.fitment_date}</span>{" "}
                  <span className="text-[#3f4b52]">{b.fitment_time || ""}</span>{" "}
                  <span className="text-[#111619]">· {b.registration}</span>{" "}
                  <span className="text-[#3f4b52]">· {b.client_name}</span>
                </div>
                <span className="text-xs text-[#3f4b52]">{b.fitter_team_leader || ""}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Chase list — overdue invoices grouped by customer with one-tap WhatsApp */}
      <ChaseListWidget chase={chase} loading={loading} />
    </div>
  );
};

const ChaseListWidget = ({ chase, loading }) => {
  const total = Number(chase?.total_overdue || 0);
  const rows = chase?.customers || [];
  const buildMsg = (c) =>
    `Hi ${(c.name || "").split(" ")[0] || "there"}, just a friendly reminder — you currently have ` +
    `R ${Number(c.total_overdue || 0).toFixed(2)} outstanding with Diesel & Dust ` +
    `(${c.invoice_count} invoice${c.invoice_count === 1 ? "" : "s"}, oldest ${c.oldest_days} days). ` +
    `Please let us know when we can expect payment or if you'd like a fresh statement. Thanks!`;

  return (
    <div className="mt-10 bg-white border border-[#cfd7db] rounded-md shadow-sm" data-testid="chase-list-widget">
      <div className="px-5 py-4 border-b border-[#cfd7db] flex items-center justify-between">
        <div>
          <div className="font-display uppercase tracking-widest text-xs text-[#dc2626] flex items-center gap-2">
            <FileWarning size={14} /> Chase list — overdue accounts
          </div>
          <div className="mt-1 text-sm text-[#3f4b52]">
            {loading ? (
              "Loading overdue accounts…"
            ) : rows.length === 0 ? (
              <span className="text-[#10b981]">Nothing overdue — nice work.</span>
            ) : (
              <>
                <b className="text-[#dc2626]">{money(total)}</b> overdue across{" "}
                <b>{chase?.customer_count || 0}</b> customer{(chase?.customer_count || 0) === 1 ? "" : "s"}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="chase-run-reminders"
            onClick={async () => {
              if (!window.confirm(
                "Send an automatic follow-up email to every overdue customer with an email on file?\n" +
                "(Customers already reminded in the last 7 days are skipped.)",
              )) return;
              try {
                const res = await import("../../lib/api").then((m) =>
                  m.adminApi.runReminders({ min_age_days: 7 }),
                );
                const s = res.sent || 0;
                const k = res.skipped || 0;
                const f = res.failed || 0;
                (await import("sonner")).toast.success(
                  `Reminders: ${s} sent · ${k} skipped · ${f} failed`,
                );
              } catch (e) {
                (await import("sonner")).toast.error(
                  e?.response?.data?.detail || "Reminder run failed",
                );
              }
            }}
            className="text-xs uppercase font-display tracking-widest px-3 h-8 border border-[#f28a16] text-[#f28a16] rounded-md hover:bg-[#fff7ed] inline-flex items-center gap-1"
          >
            Run reminders now
          </button>
          <Link to="/admin/aging" className="text-xs text-[#f28a16] font-semibold hover:underline">
            Full aging report →
          </Link>
        </div>
      </div>

      {!loading && rows.length > 0 && (
        <ul className="divide-y divide-[#eef2f4]">
          {rows.map((c) => (
            <li
              key={c.customer_id || c.code || c.name}
              className="px-5 py-3 flex items-center gap-3 flex-wrap"
              data-testid={`chase-row-${c.code || c.customer_id || "row"}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {c.code && <span className="font-mono text-xs font-bold text-[#f28a16]">{c.code}</span>}
                  <span className="font-display font-bold text-[#111619] truncate">{c.name}</span>
                  {c.oldest_days > 60 && (
                    <span className="text-[10px] uppercase tracking-widest bg-[#fee2e2] text-[#dc2626] px-2 py-0.5 rounded">
                      {c.oldest_days}d old
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#3f4b52] mt-0.5">
                  {c.invoice_count} outstanding invoice{c.invoice_count === 1 ? "" : "s"}
                  {c.phone ? <> · {c.phone}</> : <> · <span className="text-[#dc2626]">no phone on file</span></>}
                </div>
              </div>
              <div className="text-right">
                <div className="font-display font-black text-lg text-[#dc2626]">{money(c.total_overdue)}</div>
              </div>
              <div className="flex items-center gap-2">
                {c.customer_id && (
                  <Link
                    to={`/admin/customers/${c.customer_id}`}
                    className="text-xs px-3 h-8 rounded-md border border-[#cfd7db] text-[#3f4b52] font-display uppercase tracking-wider inline-flex items-center gap-1 hover:bg-[#f5f6f6]"
                    data-testid={`chase-open-${c.code || c.customer_id}`}
                  >
                    Statement
                  </Link>
                )}
                {c.phone && (
                  <a
                    href={`tel:${c.phone}`}
                    className="text-xs px-3 h-8 rounded-md border border-[#cfd7db] text-[#3f4b52] font-display uppercase tracking-wider inline-flex items-center gap-1 hover:bg-[#f5f6f6]"
                    data-testid={`chase-call-${c.code || c.customer_id}`}
                  >
                    <Phone size={12} /> Call
                  </a>
                )}
                <button
                  onClick={() => openWhatsApp(c.phone, buildMsg(c))}
                  className="text-xs px-3 h-8 rounded-md bg-[#25d366] text-white font-display uppercase tracking-wider inline-flex items-center gap-1 hover:brightness-110"
                  data-testid={`chase-whatsapp-${c.code || c.customer_id}`}
                >
                  <MessageCircle size={12} /> WhatsApp
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminDashboard;
